import React, { createContext, PropsWithChildren, useReducer } from "react";
import OnboardingDetails from "manifest-core/classes/employerDashboard/onboardingDetails";
import CompanyDetails from "manifest-core/classes/employerDashboard/companyDetails";
import LaunchDetails from "manifest-core/classes/employerDashboard/launchDetails";
import User, { Role } from "manifest-core/classes/employerDashboard/user";
import { Plan } from "manifest-core/classes/employerDashboard/plan";
import { PlanAdministrator } from "manifest-core/classes/employerDashboard/planAdministrator";
import { isDevelopmentEnvironment, isLocalEnvironment } from "../../env";
import OnboardingReducer from "./userReducer";
import { UserState } from "./userState";
import API from "../../utils/api";
import { Actions } from "./userActions";

export type OnboardingProviderValue = ReturnType<typeof createProviderValue>;

export const localAPI = {
  getCompanyLogo: API.company.getCompanyLogo,
  removeCompanyLogo: API.company.removeCompanyLogo,
  getOnboardingStatus: API.user.getOnboardingStatus,
  getCompanyDetails: API.company.getCompanyDetails,
  getLaunchDetails: API.launchDetails.getLaunchDetails,
  getPayrollProviders: API.user.getPayrollProviders,
  resendVerificationEmail: API.user.resendVerificationEmail,
  updateCompanyDetails: API.company.updateCompanyDetails,
  uploadCompanyLogo: API.company.uploadCompanyLogo,
  updateLaunchDetails: API.launchDetails.updateLaunchDetails,
  updatePlan: API.plan.updatePlan,
  getPlanAdministrator: API.plan.getPlanAdministrator,
  createTeamMember: API.team.createMember,
  updateTeamMember: API.team.updateMember,
  updateUser: API.user.updateUser,
  getUser: API.user.getUser,
  updatePassword: API.user.updatePassword,
  updatePlanAdministrator: API.plan.updatePlanAdministrator,
  removePlan: API.plan.removePlan,
  getAllPlans: API.plan.getAllPlans,
  getTeamMembers: API.team.getMembers,
  removeTeamMember: API.team.removeMember,
  removePlanDocument: API.plan.removePlanDocument,
  removePlanInvestmentDocument: API.plan.removePlanInvestmentDocument,
  uploadPlanDocument: API.plan.uploadPlanDocument,
  uploadInvestmentDocument: API.plan.uploadInvestmentDocument,
  uploadParticipantDocument: API.launchDetails.uploadParticipantDocument,
  removeParticipantDocument: API.launchDetails.removeParticipantDocument,
  resendTeamInvite: API.team.resendInvite,
  getRecordKeeperList: API.plan.getRecordKeeperList,
  createScheduleCall: API.user.schedulePhoneCall,
  getScheduleCallDetails: API.user.getScheduleCallDetails,
  getFinchClientId: API.hrIntegration.getFinchClientId,
  createHRIntegration: API.hrIntegration.createHRIntegration,
};

export type OnboardingProviderAPIValue = typeof localAPI;

const createProviderValue = ({ api = localAPI }) => {
  const [state, dispatch] = useReducer(OnboardingReducer, UserState);

  const getPlanAdministrator = () => api.getPlanAdministrator();

  const updateOnboardingDetails = (newDetails: OnboardingDetails) => {
    dispatch({
      type: Actions.ONBOARDING_STATUS_FETCHED,
      payload: newDetails,
    });
    dispatch({
      type: Actions.USER_STATUS_FETCHED,
      payload: new User({
        ...state.user,
        companyName: newDetails.companyDetails?.companyName,
      }),
    });
    return newDetails;
  };

  const getOnboardingDetails = async () => {
    dispatch({
      type: Actions.FETCHING_ONBOARDING_STATUS,
    });
    const details = await api.getOnboardingStatus();
    return updateOnboardingDetails(details);
  };

  const getRecordKeeperList = async () => {
    dispatch({
      type: Actions.FETCHING_RECORD_KEEPERS_LIST,
    });
    const list = await api.getRecordKeeperList();
    dispatch({
      type: Actions.RECORD_KEEPERS_LIST_FETCHED,
      payload: list,
    });
    return list;
  };

  const getPayrollProviders = async () => {
    dispatch({
      type: Actions.FETCHING_PAYROLL_PROVIDERS,
    });
    const details = await api.getPayrollProviders();
    dispatch({
      type: Actions.PAYROLL_PROVIDERS_FETCHED,
      payload: details,
    });
    return details;
  };

  const getPlanTypeOptions = async () => {
    return ["401(k)", "403(b)", "457", "TSP", "Profit Sharing Plan", "Money Purchase Plan"];
  };

  const updateIntegrationDetails = async (integrationDetails: boolean) => {
    dispatch({
      type: Actions.UPDATE_INTEGRATION,
      payload: integrationDetails,
    });
  };

  const resendVerificationEmail = async (email: string) => {
    return api.resendVerificationEmail(email);
  };

  const getRoleOptions = async () => {
    return [Role.Admin, Role.User];
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sendAdminInvite = async (email: string) => {
    return new Promise((resolve) => {
      setTimeout(resolve, 1000);
    });
  };

  const updateCompanyDetails = async (companyDetails: CompanyDetails) => {
    const newCompanyDetails = await api.updateCompanyDetails(companyDetails);
    const newDetails = new OnboardingDetails({
      ...state.onboardingDetails,
      companyDetails: newCompanyDetails,
    });
    updateOnboardingDetails(newDetails);
    return newDetails.companyDetails;
  };

  const updateCompanyLogo = async (companyLogo: File) => {
    await api.uploadCompanyLogo(companyLogo);
    const newDetails = new OnboardingDetails({
      ...state.onboardingDetails,
      companyDetails: new CompanyDetails({
        ...(state.onboardingDetails?.companyDetails || {}),
        _originalData: undefined,
        logoFileName: companyLogo.name,
      }),
    });
    updateOnboardingDetails(newDetails);
    return newDetails.companyDetails;
  };

  const removeCompanyLogo = async (logoFileName: string) => {
    await api.removeCompanyLogo(logoFileName);
    const newDetails = new OnboardingDetails({
      ...state.onboardingDetails,
      companyDetails: new CompanyDetails({
        ...(state.onboardingDetails?.companyDetails || {}),
        _originalData: undefined,
        logoFileName: undefined,
      }),
    });
    updateOnboardingDetails(newDetails);
    return newDetails.companyDetails;
  };

  const getCompanyDetails = async () => {
    return localAPI.getCompanyDetails();
  };

  const getLaunchDetails = async () => {
    return localAPI.getLaunchDetails();
  };

  const getUser = async () => {
    dispatch({
      type: Actions.FETCHING_USER_STATUS,
    });
    try {
      const details = await api.getUser();
      if (isLocalEnvironment() || isDevelopmentEnvironment()) {
        details.emailVerified = true;
      }
      dispatch({
        type: Actions.USER_STATUS_FETCHED,
        payload: details,
      });
      return details;
    } catch (e) {
      dispatch({
        type: Actions.USER_STATUS_FETCHED,
        payload: null,
      });
      return null;
    }
  };

  const updateUser = async (user: User) => {
    await api.updateUser(user);
    return getUser();
  };

  const updateLaunchDetails = async (launchDetails: LaunchDetails) => {
    const newLaunchDetails = await api.updateLaunchDetails(launchDetails);
    const newDetails = new OnboardingDetails({
      ...state.onboardingDetails,
      launchDetails: newLaunchDetails,
    });
    updateOnboardingDetails(newDetails);
    return newDetails.launchDetails;
  };

  const getPlans = async () => {
    const allPlans = await api.getAllPlans();
    updateOnboardingDetails(
      new OnboardingDetails({
        ...state.onboardingDetails,
        plans: allPlans,
      })
    );
    return allPlans;
  };

  const removePlan = async (plan: Plan) => {
    await api.removePlan(plan);
    return getPlans();
  };

  const updatePlan = async (plan: Plan, updateState = false) => {
    if (!plan.hasChanged) return plan;
    const newPlan = await api.updatePlan(plan);
    if (plan._planFilesToBeUploaded.length) {
      await Promise.all(
        plan._planFilesToBeUploaded.map((file) => {
          return api.uploadPlanDocument({ planId: newPlan.planId, file });
        })
      );
    }
    if (plan._planInvestmentFilesToBeUploaded.length) {
      await Promise.all(
        plan._planInvestmentFilesToBeUploaded.map((file) => {
          return api.uploadInvestmentDocument({ planId: newPlan.planId, file });
        })
      );
    }
    if (updateState) {
      await getPlans();
    }
    return newPlan;
  };

  const updatePlanAdministrator = async (details: PlanAdministrator, updateState = true) => {
    const planAdministrator = await api.updatePlanAdministrator(details);
    if (updateState) {
      updateOnboardingDetails(
        new OnboardingDetails({
          ...state.onboardingDetails,
          planAdminDetails: planAdministrator,
        })
      );
    }
    return planAdministrator;
  };

  const updatePassword = async (params: { previousPassword: string; newPassword: string }) => {
    await api.updatePassword(params);
    return getUser();
  };

  const uploadPlanDocument = async (params: { planId: number; file: File }) => {
    const { planId, file } = params;
    await api.uploadPlanDocument({ planId, file });

    // Manually add the document since the API does not return the new plan details response
    const currentPlanIndex = state.onboardingDetails!.plans.findIndex((plan) => plan.planId === planId)!;
    const currentPlan = state.onboardingDetails!.plans[currentPlanIndex];
    currentPlan.planDocumentFileNames.push(file.name);
    state.onboardingDetails!.plans[currentPlanIndex] = currentPlan;
    const newDetails = new OnboardingDetails({
      ...state.onboardingDetails,
      plans: [...state.onboardingDetails!.plans],
    });
    updateOnboardingDetails(newDetails);
    return newDetails.plans;
  };

  const uploadInvestmentDocument = async (params: { planId: number; file: File }) => {
    const { planId, file } = params;
    await api.uploadInvestmentDocument({ planId, file });

    // Manually add the document since the API does not return the new plan details response
    const currentPlanIndex = state.onboardingDetails!.plans.findIndex((plan) => plan.planId === planId)!;
    const currentPlan = state.onboardingDetails!.plans[currentPlanIndex];
    currentPlan.planInvestmentMenuFileNames.push(file.name);
    state.onboardingDetails!.plans[currentPlanIndex] = currentPlan;
    const newDetails = new OnboardingDetails({
      ...state.onboardingDetails,
      plans: [...state.onboardingDetails!.plans],
    });
    updateOnboardingDetails(newDetails);
    return newDetails.plans;
  };

  const removePlanDocument = async (params: { planId: number; fileName: string }) => {
    const { planId, fileName } = params;
    await api.removePlanDocument({ planId, fileName });

    // Manually remove the document since the API does not return the new plan details response
    const currentPlanIndex = state.onboardingDetails!.plans.findIndex((plan) => plan.planId === planId)!;
    const currentPlan = state.onboardingDetails!.plans[currentPlanIndex];
    currentPlan.planDocumentFileNames = currentPlan.planDocumentFileNames.filter(
      (existingFileName) => existingFileName !== fileName
    );
    state.onboardingDetails!.plans[currentPlanIndex] = currentPlan;
    const newDetails = new OnboardingDetails({
      ...state.onboardingDetails,
      plans: [...state.onboardingDetails!.plans],
    });
    updateOnboardingDetails(newDetails);
    return newDetails.plans;
  };

  const removePlanInvestmentDocument = async (params: { planId: number; fileName: string }) => {
    const { planId, fileName } = params;
    await api.removePlanInvestmentDocument({ planId, fileName });

    // Manually remove the document since the API does not return the new plan details response
    const currentPlanIndex = state.onboardingDetails!.plans.findIndex((plan) => plan.planId === planId)!;
    const currentPlan = state.onboardingDetails!.plans[currentPlanIndex];
    currentPlan.planDocumentFileNames = currentPlan.planInvestmentMenuFileNames.filter(
      (existingFileName) => existingFileName !== fileName
    );
    state.onboardingDetails!.plans[currentPlanIndex] = currentPlan;
    const newDetails = new OnboardingDetails({
      ...state.onboardingDetails,
      plans: [...state.onboardingDetails!.plans],
    });
    updateOnboardingDetails(newDetails);
    return newDetails.plans;
  };

  const uploadParticipantDocument = async (file: File) => {
    await api.uploadParticipantDocument(file);
    const newDetails = new OnboardingDetails({
      ...state.onboardingDetails,
      launchDetails: new LaunchDetails({
        ...state.onboardingDetails!.launchDetails,
        _originalData: undefined,
        participantFileName: file.name,
      }),
    });
    updateOnboardingDetails(newDetails);
    return newDetails.launchDetails;
  };

  const removeParticipantDocument = async (fileName: string) => {
    await api.removeParticipantDocument(fileName);
    const newDetails = new OnboardingDetails({
      ...state.onboardingDetails,
      launchDetails: new LaunchDetails({
        ...state.onboardingDetails!.launchDetails,
        _originalData: undefined,
        participantFileName: undefined,
      }),
    });
    updateOnboardingDetails(newDetails);
    return newDetails.launchDetails;
  };

  const addTeamMember = async (member: User) => {
    const newUser = await api.createTeamMember(member);
    const newDetails = new OnboardingDetails({
      ...state.onboardingDetails,
      teamMembers: [...(state.onboardingDetails?.teamMembers || []), newUser],
    });
    updateOnboardingDetails(newDetails);
    return newUser;
  };

  const updateTeamMember = async (member: User) => {
    const updatedUser = await api.updateTeamMember(member);
    const memberFoundIndex = state.onboardingDetails!.teamMembers.findIndex(
      (existingMember) => existingMember.userDetailsId === member.userDetailsId
    )!;
    state.onboardingDetails!.teamMembers[memberFoundIndex] = member;
    const newDetails = new OnboardingDetails({
      ...state.onboardingDetails,
      teamMembers: [...state.onboardingDetails!.teamMembers],
    });
    updateOnboardingDetails(newDetails);
    return updatedUser;
  };

  const removeTeamMember = async (member: User) => {
    await api.removeTeamMember(member);
    const memberFoundIndex = state.onboardingDetails!.teamMembers.findIndex(
      (existingMember) => existingMember.userDetailsId === member.userDetailsId
    )!;
    state.onboardingDetails!.teamMembers.splice(memberFoundIndex, 1);
    const newDetails = new OnboardingDetails({
      ...state.onboardingDetails,
      teamMembers: [...state.onboardingDetails!.teamMembers],
    });
    updateOnboardingDetails(newDetails);
    return true;
  };

  const resendTeamMemberInvite = async (member: User) => {
    return api.resendTeamInvite(member);
  };

  const clearData = () => {
    dispatch({
      type: Actions.CLEAR_DATA,
    });
  };

  const createScheduleCall = async (eventUri: string) => {
    return api.createScheduleCall(eventUri);
  };

  const getScheduledCallDetails = async () => {
    return api.getScheduleCallDetails();
  };

  const getFinchClientId = async () => {
    return api.getFinchClientId();
  };

  const createHRIntegration = async (code: string) => {
    return api.createHRIntegration(code);
  };

  return {
    ...state,
    clearData,
    getCompanyLogo: api.getCompanyLogo,
    getCompanyDetails,
    getLaunchDetails,
    getOnboardingDetails,
    getPayrollProviders,
    getPlanTypeOptions,
    getRoleOptions,
    getUser,
    sendAdminInvite,
    removeCompanyLogo,
    removePlan,
    removePlanDocument,
    removePlanInvestmentDocument,
    resendVerificationEmail,
    updateCompanyDetails,
    updateLaunchDetails,
    updatePlanAdministrator,
    updateTeamMember,
    updateUser,
    updatePassword,
    uploadPlanDocument,
    uploadInvestmentDocument,
    updateCompanyLogo,
    uploadParticipantDocument,
    removeParticipantDocument,
    addTeamMember,
    removeTeamMember,
    resendTeamMemberInvite,
    getRecordKeeperList,
    updateOnboardingDetails,
    updatePlan,
    getPlans,
    createScheduleCall,
    getScheduledCallDetails,
    updateIntegrationDetails,
    getFinchClientId,
    createHRIntegration,
    getPlanAdministrator,
  };
};

export const UserContext = createContext({} as OnboardingProviderValue);

const UserProvider = (props: PropsWithChildren<any>) => {
  const providerValue = createProviderValue(props);
  return <UserContext.Provider value={providerValue}>{props.children}</UserContext.Provider>;
};

export default UserProvider;
